import { Clear, InfoOutlined } from "@mui/icons-material";
import {
  Autocomplete,
  Chip,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  TextField,
  Tooltip,
} from "@mui/material";
import { Control, Controller } from "react-hook-form";

type DeRiskMultipleControllerProps = {
  control: Control<any>;
  name: string;
  required?: boolean;
};

const OPTIONS = [
  {
    value: "Competitor",
    label: "Competitor",
    tip: "Refers to the threat of losing a deal to a rival vendor. The risk is that the prospect may perceive a competitor's solution as more cost-effective, easier to use, or better integrated with their existing software suite.",
  },
  {
    value: "Product / Feature / Service",
    label: "Product / Feature / Service",
    tip: "Risk that the current product capabilities do not fully meet the customer's specific technical or business needs. It can also refer to 'roadmap risk,' where a sale is contingent on a feature that is not yet available or is delayed in development.",
  },
  {
    value: "Political / Executive",
    label: "Political / Executive",
    tip: 'Risk relates to the internal dynamics and decision-making hierarchy within the client\'s organization. It occurs when there is a lack of "buy-in" from key executives, a change in leadership during the sales cycle, or internal disagreements between departments that could stall or kill the deal.',
  },
  {
    value: "Low Usage / Adoption",
    label: "Low Usage / Adoption",
    tip: "Risk if the product is purchased, the end-users will not actually use it. Low adoption is a significant threat to long-term success because it leads to poor Return on Investment (ROI) for the customer, making it highly unlikely they will renew their contract or purchase more licenses in the future.",
  },
  {
    value: "Financial [Pricing / Restructure / Resizing / Downsell]",
    label: "Financial [Pricing / Restructure / Resizing / Downsell]",
    tip: "Risks related to the customer's ability to pay or secure funding. It includes budget cuts, changes in the customer's financial health, or the price point of the offer is not aligned with the Customer's budget.",
  },
  {
    value: "Partner / Customer Dynamics",
    label: "Partner / Customer Dynamics",
    tip: "This includes risks related to partner alignment, satisfaction, and engagement, which can influence renewal success.",
  },
];

const DeRiskMultipleController = ({
  control,
  name,
  required = false,
}: DeRiskMultipleControllerProps) => {
  return (
    <Controller
      control={control}
      name={name}
      defaultValue={[]}
      render={({ field }) => (
        <Autocomplete
          {...field}
          multiple={true}
          disableCloseOnSelect={true}
          size="small"
          options={OPTIONS}
          getOptionLabel={(option: any) => option.label}
          value={OPTIONS.filter((o) => (field.value ?? []).includes(o.value))}
          onChange={(_, value) => {
            field.onChange(value.map((v) => v.value));
          }}
          renderTags={(value, getTagProps) => {
            if (!value.length) return null;

            const visibleTagCount = 2;
            return (
              <>
                {value.slice(0, visibleTagCount).map((item, index) => {
                  const { key, ...rest } = getTagProps({ index });
                  return (
                    <Chip
                      key={key}
                      size="small"
                      label={item.label}
                      {...rest}
                    />
                  );
                })}
                {value.length > visibleTagCount && (
                  <Tooltip
                    title={
                      <List
                        disablePadding
                        dense
                      >
                        {value.slice(visibleTagCount).map((item, index) => (
                          <ListItem
                            dense
                            key={item.value}
                          >
                            <ListItemText primary={item.label} />
                            <ListItemSecondaryAction style={{ right: 4 }}>
                              <IconButton
                                size="small"
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                }}
                                onClick={
                                  (
                                    getTagProps({
                                      index: index + visibleTagCount,
                                    }) as any
                                  )?.onDelete
                                }
                              >
                                <Clear />
                              </IconButton>
                            </ListItemSecondaryAction>
                          </ListItem>
                        ))}
                      </List>
                    }
                  >
                    <Chip
                      size="small"
                      label={`${value.length - visibleTagCount} more`}
                    />
                  </Tooltip>
                )}
              </>
            );
          }}
          renderInput={(params) => {
            return (
              <TextField
                {...params}
                style={{ width: "480px" }}
              />
            );
          }}
          renderOption={(props, option) => {
            const { key, ...rest } = props;
            return (
              <li
                {...rest}
                key={option.value}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: "480px",
                  }}
                >
                  <span>{option.label}</span>
                  <Tooltip title={option.tip}>
                    <InfoOutlined color="info" />
                  </Tooltip>
                </div>
              </li>
            );
          }}
        />
      )}
      rules={{
        required: {
          message: "The field is required.",
          value: required,
        },
      }}
    />
  );
};

export default DeRiskMultipleController;
